import { getCurrencySymbol, getPriceString, getRewardText, twoDecimalFormatter } from "halifax";
import { Amount, RewardsPrice } from "redmond";
import { REFUND_PRICES_FIAT_AND_REWARDS } from "./constants";

export const getCombinedRefundPricesString = (
  refundPricesInFiatOnly?: string,
  refundPricesInRewardsOnly?: string
) => {
  if (refundPricesInRewardsOnly && refundPricesInFiatOnly) {
    return REFUND_PRICES_FIAT_AND_REWARDS(
      refundPricesInFiatOnly,
      refundPricesInRewardsOnly
    );
  }

  if (refundPricesInRewardsOnly) {
    return refundPricesInRewardsOnly;
  }

  if (refundPricesInFiatOnly) {
    return refundPricesInFiatOnly;
  }

  // Note the impossible edge case of fiat value of 0 and also no rewards will also return "" as a placeholder.
  return null;
};

export const refundPricesCombinationToString = (prices: {
  rewardsCredit?: RewardsPrice;
  userCardCredit?: Amount;
  travelWalletCredits?: Amount;
  totalAmount?: Amount;
}): string | null => {
  let refundPricesInFiatOnly;
  let refundPricesInRewardsOnly;
  let refundPricesInTravelCreditsOnly;

  // Use Math.abs() to ensure positive values for display (refunds may come as negative from backend)
  if (prices.userCardCredit && Math.abs(prices.userCardCredit.amount) > 0) {
    refundPricesInFiatOnly = getPriceString({
      price: Math.abs(prices.userCardCredit.amount),
      currencySymbol: getCurrencySymbol(prices.userCardCredit.currency),
      priceFormatter: twoDecimalFormatter,
    });
  }

  if (prices.rewardsCredit) {
    // For rewards, ensure the value is positive
    const rewardsValue = Math.abs(prices.rewardsCredit.value);
    if (rewardsValue > 0) {
      refundPricesInRewardsOnly = getRewardText({
        reward: {
          ...prices.rewardsCredit,
          value: rewardsValue,
        }
      });
    }
  }

  if (prices.travelWalletCredits && Math.abs(prices.travelWalletCredits.amount) > 0) {
    // Use Math.abs() to ensure positive values for display (refunds may come as negative from backend)
    const travelCreditsAmount = Math.abs(prices.travelWalletCredits.amount);
    refundPricesInTravelCreditsOnly = `${getPriceString({
      price: travelCreditsAmount,
      currencySymbol: getCurrencySymbol(prices.travelWalletCredits.currency),
      priceFormatter: twoDecimalFormatter,
    })} in travel credit`;
  }

  // Fallback to totalAmount if neither userCardCredit, rewardsCredit, nor travelWalletCredits are available
  // This handles the case where bookings were paid with credits only
  if (!refundPricesInFiatOnly && !refundPricesInRewardsOnly && !refundPricesInTravelCreditsOnly && prices.totalAmount) {
    const totalAmount = Math.abs(prices.totalAmount.amount);
    if (totalAmount > 0) {
      refundPricesInFiatOnly = getPriceString({
        price: totalAmount,
        currencySymbol: getCurrencySymbol(prices.totalAmount.currency),
        priceFormatter: twoDecimalFormatter,
      });
    }
  }

  // Combine all refund types in the order: rewards, travel credits, cash
  const refundTypes: string[] = [
    refundPricesInRewardsOnly,
    refundPricesInTravelCreditsOnly,
    refundPricesInFiatOnly,
  ].filter((r): r is string => r !== undefined && r !== null && r !== "");

  if (refundTypes.length === 0) {
    return null;
  }

  if (refundTypes.length === 1) {
    return refundTypes[0] ?? null;
  }

  if (refundTypes.length === 2) {
    return refundTypes.join(" and ");
  }

  // 3 types: "rewards, travel credits, and cash"
  const last = refundTypes.pop();
  return refundTypes.join(", ") + ", and " + (last ?? "");
};
